import { reportingPrisma as prisma } from "client";

const translationSelect = { select: { name: true, languageId: true } };
const meetingRoomTranslationSelect = {
  select: { name: true, location: true, languageId: true },
};

type NameTranslation = { name: string; languageId: string };
type SlimTicket = Awaited<ReturnType<typeof mapSlimTicket>>;

function translationsOf(languageId?: string) {
  return languageId
    ? { where: { languageId }, ...translationSelect }
    : translationSelect;
}

function pickName(translations: NameTranslation[], languageId?: string) {
  if (languageId) {
    const match = translations.find(
      (translation) => translation.languageId === languageId
    );
    if (match) return match.name;
  }
  return translations[0]?.name;
}

function uniqueIds(ids: Array<string | null | undefined>) {
  return [...new Set(ids.filter((id): id is string => Boolean(id)))];
}

function mapById<T extends { id: string }>(items: T[]) {
  return new Map(items.map((item) => [item.id, item]));
}

function lookupByIds<T>(
  ids: string[] | null | undefined,
  byId: Map<string, T>
) {
  return (ids ?? []).flatMap((id) => {
    const item = byId.get(id);
    return item ? [item] : [];
  });
}

function lookupAllByIds<T>(
  ids: string[] | null | undefined,
  byId: Map<string, T[]>
) {
  return (ids ?? []).flatMap((id) => byId.get(id) ?? []);
}

async function getLanguageId(locale?: string) {
  if (!locale) return undefined;
  const language = await prisma.language.findUnique({
    where: { code: locale },
    select: { id: true },
  });
  return language?.id;
}

async function getEventVenues(eventId: string) {
  return prisma.venue.findMany({
    where: { eventIds: { has: eventId } },
    select: {
      id: true,
      translations: translationSelect,
    },
  });
}

async function getSlimZones(venueIds: string[]) {
  if (venueIds.length === 0) return [];

  return prisma.zone.findMany({
    where: {
      venueId: { in: venueIds },
      deletedAt: null,
    },
    select: {
      id: true,
      type: true,
      venueId: true,
      color: true,
      capacity: true,
      enableZoneAccessControl: true,
      enableByTicketType: true,
      enableByUserType: true,
      enableByInvitation: true,
      allowAccessWhenFull: true,
      accessibleBookableItemIds: true,
      accessibleUserGroupIds: true,
      accessibleInvitationIds: true,
      doorAgentIds: true,
      translations: translationSelect,
    },
  });
}

const ticketSelect = (languageId?: string) => ({
  id: true,
  bookableItemId: true,
  bookableItem: {
    select: {
      color: true,
      translations: translationsOf(languageId),
    },
  },
});

function mapSlimTicket(ticket: {
  id: string;
  bookableItemId: string;
  bookableItem: {
    color: string | null;
    translations: NameTranslation[];
  } | null;
}) {
  return {
    id: ticket.id,
    bookableItemId: ticket.bookableItemId,
    translations: ticket.bookableItem?.translations ?? [],
    bookableItem: { color: ticket.bookableItem?.color ?? null },
    color: ticket.bookableItem?.color ?? null,
  };
}

function toPublicTicket(ticket: SlimTicket) {
  const { bookableItemId: _bookableItemId, ...publicTicket } = ticket;
  return publicTicket;
}

function indexTicketsByBookableItemId(tickets: SlimTicket[]) {
  const byBookableItemId = new Map<
    string,
    ReturnType<typeof toPublicTicket>[]
  >();

  for (const ticket of tickets) {
    if (!ticket.bookableItemId) continue;
    const existing = byBookableItemId.get(ticket.bookableItemId);
    const publicTicket = toPublicTicket(ticket);
    if (existing) {
      if (!existing.some((item) => item.id === publicTicket.id)) {
        existing.push(publicTicket);
      }
    } else {
      byBookableItemId.set(ticket.bookableItemId, [publicTicket]);
    }
  }

  return byBookableItemId;
}

async function getSlimTickets(eventId: string, languageId?: string) {
  const tickets = await prisma.ticket.findMany({
    where: {
      eventId,
      OR: [{ deletedAt: { isSet: false } }, { deletedAt: null }],
    },
    select: ticketSelect(languageId),
  });

  return tickets.map(mapSlimTicket);
}

async function getSlimTicketsByBookableItemIds(
  bookableItemIds: string[],
  languageId?: string
) {
  if (bookableItemIds.length === 0) return [];

  const tickets = await prisma.ticket.findMany({
    where: { bookableItemId: { in: bookableItemIds } },
    select: ticketSelect(languageId),
  });

  return tickets.map(mapSlimTicket);
}

async function getSlimUserGroups(eventId: string, languageId?: string) {
  const groups = await prisma.participantGroup.findMany({
    where: { eventId },
    select: {
      id: true,
      color: true,
      translations: translationsOf(languageId),
    },
  });

  return groups.map((group) => ({
    ...group,
    name: pickName(group.translations, languageId),
  }));
}

async function getSlimInvitations(eventId: string, languageId?: string) {
  return prisma.invitation.findMany({
    where: { eventId },
    select: {
      id: true,
      translations: translationsOf(languageId),
    },
  });
}

const doorAgentSelect = { id: true, name: true, email: true };

async function getSlimDoorAgents(eventId: string) {
  return prisma.user.findMany({
    where: {
      AND: [
        {
          accessibleWhiteLabel: {
            events: {
              some: { id: eventId },
            },
          },
        },
        {
          OR: [
            {
              profiles: {
                some: {
                  AND: [
                    { event: { id: eventId } },
                    { roleV2: { name: "DOOR_AGENT" } },
                  ],
                },
              },
            },
            { roleV2: { name: "DOOR_AGENT" } },
            {
              userRoleAssignments: {
                some: {
                  role: { name: "DOOR_AGENT" },
                  entityId: eventId,
                },
              },
            },
          ],
        },
      ],
    },
    select: doorAgentSelect,
  });
}

async function getSlimUsersByIds(ids: string[]) {
  if (ids.length === 0) return [];

  return prisma.user.findMany({
    where: { id: { in: ids } },
    select: doorAgentSelect,
  });
}

async function getSlimCheckInRules(eventId: string, venueIds: string[]) {
  return prisma.checkInRules.findMany({
    where: {
      AND: [
        { OR: [{ deletedAt: null }, { deletedAt: { isSet: false } }] },
        {
          OR: [
            { eventId },
            ...(venueIds.length > 0 ? [{ venueId: { in: venueIds } }] : []),
          ],
        },
      ],
    },
    select: {
      id: true,
      name: true,
      eventId: true,
      venueId: true,
      zonesIds: true,
      numberOfScans: true,
      minsBetweenScans: true,
      isCheckoutRequired: true,
      autoCheckOut: true,
      isDefault: true,
      validationStart: true,
      validationEnd: true,
      createdAt: true,
      updatedAt: true,
    },
  });
}

async function getSlimMeetingRooms(venueIds: string[]) {
  if (venueIds.length === 0) return [];

  return prisma.meetingRoom.findMany({
    where: { venueId: { in: venueIds } },
    select: {
      id: true,
      color: true,
      capacity: true,
      venueId: true,
      createdAt: true,
      translations: meetingRoomTranslationSelect,
      venue: {
        select: {
          id: true,
          translations: translationSelect,
        },
      },
    },
    orderBy: { createdAt: "desc" },
  });
}

/**
 * Admin venue Zones tab. Indexed lookups + in-memory joins instead of nested
 * Prisma includes (invitation attendees, group members, WL event walks).
 */
export async function getVenueZonesTabData(eventId: string, locale?: string) {
  const languagePromise = getLanguageId(locale);
  const venuesPromise = getEventVenues(eventId);

  const ticketsPromise = languagePromise.then((languageId) =>
    getSlimTickets(eventId, languageId)
  );
  const groupsPromise = languagePromise.then((languageId) =>
    getSlimUserGroups(eventId, languageId)
  );
  const invitationsPromise = languagePromise.then((languageId) =>
    getSlimInvitations(eventId, languageId)
  );
  const zonesPromise = venuesPromise.then((venues) =>
    getSlimZones(venues.map((venue) => venue.id))
  );
  const doorAgentsPromise = getSlimDoorAgents(eventId);
  const assignedTicketsPromise = Promise.all([
    zonesPromise,
    ticketsPromise,
    languagePromise,
  ]).then(([zones, eventTickets, languageId]) => {
    const knownBookableItemIds = new Set(
      eventTickets.map((ticket) => ticket.bookableItemId)
    );
    const missingBookableItemIds = uniqueIds(
      zones.flatMap((zone) => zone.accessibleBookableItemIds ?? [])
    ).filter((id) => !knownBookableItemIds.has(id));

    return getSlimTicketsByBookableItemIds(missingBookableItemIds, languageId);
  });
  const assignedAgentsPromise = Promise.all([
    zonesPromise,
    doorAgentsPromise,
  ]).then(([zones, doorAgents]) => {
    const knownIds = new Set(doorAgents.map((user) => user.id));
    const missingIds = uniqueIds(
      zones.flatMap((zone) => zone.doorAgentIds ?? [])
    ).filter((id) => !knownIds.has(id));

    return getSlimUsersByIds(missingIds);
  });

  const [
    languageId,
    venues,
    tickets,
    assignedTickets,
    userGroups,
    invitations,
    zones,
    doorManagementUshrers,
    assignedAgents,
  ] = await Promise.all([
    languagePromise,
    venuesPromise,
    ticketsPromise,
    assignedTicketsPromise,
    groupsPromise,
    invitationsPromise,
    zonesPromise,
    doorAgentsPromise,
    assignedAgentsPromise,
  ]);

  const allTickets = [...tickets, ...assignedTickets];
  const venueById = mapById(
    venues.map((venue) => ({
      ...venue,
      name: pickName(venue.translations, languageId),
    }))
  );
  const ticketsByBookableItemId = indexTicketsByBookableItemId(allTickets);
  const groupById = mapById(userGroups);
  const invitationById = mapById(invitations);
  const agentById = mapById([...doorManagementUshrers, ...assignedAgents]);

  return {
    zones: zones.map((zone) => ({
      id: zone.id,
      type: zone.type,
      venueId: zone.venueId,
      color: zone.color,
      capacity: zone.capacity,
      enableZoneAccessControl: zone.enableZoneAccessControl,
      enableByTicketType: zone.enableByTicketType,
      enableByUserType: zone.enableByUserType,
      enableByInvitation: zone.enableByInvitation,
      allowAccessWhenFull: zone.allowAccessWhenFull,
      translations: zone.translations,
      name: pickName(zone.translations, languageId),
      venue: zone.venueId ? (venueById.get(zone.venueId) ?? null) : null,
      accessibleTickets: lookupAllByIds(
        zone.accessibleBookableItemIds,
        ticketsByBookableItemId
      ),
      accessibleUserGroups: lookupByIds(zone.accessibleUserGroupIds, groupById),
      accessibleInvitations: lookupByIds(
        zone.accessibleInvitationIds,
        invitationById
      ),
      doorAgents: lookupByIds(zone.doorAgentIds, agentById),
    })),
    tickets: allTickets.map(toPublicTicket),
    doorManagementUshrers,
    userGroups,
    invitations,
  };
}

export async function getVenueCheckInRulesTabData(
  eventId: string,
  locale?: string
) {
  const languagePromise = getLanguageId(locale);
  const venuesPromise = getEventVenues(eventId);
  const doorAgentsPromise = getSlimDoorAgents(eventId);

  const zonesPromise = venuesPromise.then((venues) =>
    getSlimZones(venues.map((venue) => venue.id))
  );
  const rulesPromise = venuesPromise.then((venues) =>
    getSlimCheckInRules(
      eventId,
      venues.map((venue) => venue.id)
    )
  );

  const [languageId, venues, zones, rules, doorManagementUshrers] =
    await Promise.all([
      languagePromise,
      venuesPromise,
      zonesPromise,
      rulesPromise,
      doorAgentsPromise,
    ]);

  const venueById = mapById(
    venues.map((venue) => ({
      ...venue,
      name: pickName(venue.translations, languageId),
    }))
  );
  const zoneById = mapById(
    zones.map((zone) => ({
      id: zone.id,
      type: zone.type,
      venueId: zone.venueId,
      translations: zone.translations,
      name: pickName(zone.translations, languageId),
    }))
  );

  return {
    rules: rules.map((rule) => ({
      ...rule,
      venue: rule.venueId ? (venueById.get(rule.venueId) ?? null) : null,
      zones: lookupByIds(rule.zonesIds, zoneById),
    })),
    zones: [...zoneById.values()],
    doorAgentsCount: doorManagementUshrers.length,
  };
}

export async function getVenueMeetingRoomsTabData(
  eventId: string,
  locale?: string
) {
  const languagePromise = getLanguageId(locale);
  const venuesPromise = getEventVenues(eventId);
  const meetingRoomsPromise = venuesPromise.then((venues) =>
    getSlimMeetingRooms(venues.map((venue) => venue.id))
  );

  const [languageId, meetingRooms] = await Promise.all([
    languagePromise,
    meetingRoomsPromise,
  ]);

  return {
    meetingRooms: meetingRooms.map((room) => ({
      ...room,
      venue: room.venue
        ? {
            ...room.venue,
            name: pickName(room.venue.translations, languageId),
          }
        : null,
    })),
  };
}
